/**
 * Wave RC — Report Center.
 *
 * Pick a report, set filters, Run → table + summary; export the same result as
 * PDF / Excel / CSV. Filters are POSTed, never put in the URL.
 * Standing React rule: every sub-component is declared at module scope.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Assessment, Download, PictureAsPdf, PlayArrow, TableChart } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  endOfMonth,
  endOfQuarter,
  format,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
} from 'date-fns';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';

type Column = { key: string; label: string; type?: 'date' | 'datetime' | 'number' | 'currency' };
type ReportResult = {
  key: string;
  title: string;
  description?: string;
  generatedAt: string;
  columns: Column[];
  rows: Record<string, any>[];
  groups: { label: string; count: number; metrics: Record<string, number> }[];
  summary: { label: string; value: number | string; type?: string }[];
  truncated: boolean;
  scopeNote: string | null;
  filterSummary?: string;
};
type CatalogItem = { key: string; title: string; category: string; description: string; filters: string[] };
type Filters = Record<string, any>;

const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

const PERIODS: { key: string; label: string }[] = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'last_12', label: 'Last 12 months' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom…' },
];

/** Browser-local day boundaries → ISO instants (firm's wall clock, not UTC days). */
function periodRange(period: string, customFrom: string, customTo: string): { from?: string; to?: string } {
  const now = new Date();
  const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  switch (period) {
    case 'this_month':
      return { from: startOfMonth(now).toISOString(), to: endOfDay(now).toISOString() };
    case 'last_month': {
      const m = subMonths(now, 1);
      return { from: startOfMonth(m).toISOString(), to: endOfMonth(m).toISOString() };
    }
    case 'this_quarter':
      return { from: startOfQuarter(now).toISOString(), to: endOfQuarter(now).toISOString() };
    case 'ytd':
      return { from: startOfYear(now).toISOString(), to: endOfDay(now).toISOString() };
    case 'last_12':
      return { from: startOfMonth(subMonths(now, 11)).toISOString(), to: endOfDay(now).toISOString() };
    case 'custom':
      return {
        from: customFrom ? new Date(`${customFrom}T00:00`).toISOString() : undefined,
        to: customTo ? endOfDay(new Date(`${customTo}T00:00`)).toISOString() : undefined,
      };
    default:
      return {};
  }
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
function formatCell(value: any, type?: Column['type']): string {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'date' || type === 'datetime') {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      return `${m}/${d}/${y}`;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return format(d, type === 'datetime' ? 'MM/dd/yyyy h:mm a' : 'MM/dd/yyyy');
  }
  if (type === 'currency') return currency.format(Number(value) || 0);
  return String(value);
}

async function postJson(url: string, body: unknown, fallback: string) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  return res;
}

async function getJson(url: string, fallback: string) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  return res.json();
}

// ---------------------------------------------------------------------------

interface FilterBarProps {
  item: CatalogItem;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  period: string;
  setPeriod: (p: string) => void;
  staff: any[];
  options: { practiceAreas: string[]; jurisdictions: string[] };
}

const FilterBar: React.FC<FilterBarProps> = ({ item, filters, setFilters, period, setPeriod, staff, options }) => {
  const has = (k: string) => item.filters.includes(k);
  const set = (k: string, v: any) => setFilters((f) => ({ ...f, [k]: v }));
  const person = (u: any) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
  const attorneys = staff.filter((u) => u.role === 'lawyer' || u.role === 'admin');

  return (
    <Grid container spacing={2} alignItems="center">
      {(has('from') || has('to')) && (
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-period">Period</InputLabel>
            <Select labelId="rc-period" label="Period" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      )}
      {(has('from') || has('to')) && period === 'custom' && (
        <>
          <Grid item xs={6} sm={3} md={2}>
            <TextField size="small" fullWidth type="date" label="From" InputLabelProps={{ shrink: true }}
              value={filters.customFrom || ''} onChange={(e) => set('customFrom', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField size="small" fullWidth type="date" label="To" InputLabelProps={{ shrink: true }}
              value={filters.customTo || ''} onChange={(e) => set('customTo', e.target.value)} />
          </Grid>
        </>
      )}
      {has('days') && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-days">Look ahead</InputLabel>
            <Select labelId="rc-days" label="Look ahead" value={filters.days || 30} onChange={(e) => set('days', e.target.value)}>
              {[7, 14, 30, 60, 90, 180].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('attorneyId') && (
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-atty">Attorney</InputLabel>
            <Select labelId="rc-atty" label="Attorney" value={filters.attorneyId || ''} onChange={(e) => set('attorneyId', e.target.value)}>
              <MenuItem value="">All attorneys</MenuItem>
              {attorneys.map((u) => <MenuItem key={u.id} value={u.id}>{person(u)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('performedBy') && (
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-staff">Staff member</InputLabel>
            <Select labelId="rc-staff" label="Staff member" value={filters.performedBy || ''} onChange={(e) => set('performedBy', e.target.value)}>
              <MenuItem value="">Everyone</MenuItem>
              {staff.map((u) => <MenuItem key={u.id} value={u.id}>{person(u)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('role') && (
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-role">Office area</InputLabel>
            <Select labelId="rc-role" label="Office area" value={filters.role || ''} onChange={(e) => set('role', e.target.value)}>
              <MenuItem value="">All areas</MenuItem>
              <MenuItem value="lawyer">Attorneys</MenuItem>
              <MenuItem value="paralegal">Paralegals</MenuItem>
              <MenuItem value="assistant">Legal assistants</MenuItem>
              <MenuItem value="admin">Administration</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('practiceArea') && (
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            freeSolo
            options={options.practiceAreas}
            value={filters.practiceArea || ''}
            onInputChange={(_, v) => set('practiceArea', v)}
            renderInput={(params) => <TextField {...params} label="Practice area" placeholder="All" />}
          />
        </Grid>
      )}
      {has('jurisdiction') && (
        <Grid item xs={12} sm={6} md={2}>
          <Autocomplete
            size="small"
            freeSolo
            options={options.jurisdictions}
            value={filters.jurisdiction || ''}
            onInputChange={(_, v) => set('jurisdiction', v)}
            renderInput={(params) => <TextField {...params} label="Jurisdiction" placeholder="All" />}
          />
        </Grid>
      )}
      {has('status') && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-status">Status</InputLabel>
            <Select labelId="rc-status" label="Status" value={filters.status || ''} onChange={(e) => set('status', e.target.value)}>
              <MenuItem value="">Open matters</MenuItem>
              {['open', 'pending', 'active', 'on_hold', 'closed', 'archived'].map((s) => (
                <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('activityGroup') && (
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-ag">Activity</InputLabel>
            <Select labelId="rc-ag" label="Activity" value={filters.activityGroup || ''} onChange={(e) => set('activityGroup', e.target.value)}>
              <MenuItem value="">All activity</MenuItem>
              <MenuItem value="court">Court (filings, hearings)</MenuItem>
              <MenuItem value="client">Client communication</MenuItem>
              <MenuItem value="external">Opposing counsel / third parties</MenuItem>
              <MenuItem value="litigation">Litigation</MenuItem>
              <MenuItem value="internal">Internal work</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('staleDays') && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="rc-stale">Idle after</InputLabel>
            <Select labelId="rc-stale" label="Idle after" value={filters.staleDays || 30} onChange={(e) => set('staleDays', e.target.value)}>
              {[14, 30, 60, 90].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      )}
      {has('includeClosed') && !filters.status && (
        <Grid item xs={12} sm={6} md={2}>
          <FormControlLabel
            control={<Checkbox checked={Boolean(filters.includeClosed)} onChange={(e) => set('includeClosed', e.target.checked)} />}
            label="Include closed"
          />
        </Grid>
      )}
    </Grid>
  );
};

const SummaryTiles: React.FC<{ summary: ReportResult['summary'] }> = ({ summary }) => (
  <Grid container spacing={2} sx={{ mb: 2 }}>
    {summary.map((s) => (
      <Grid item xs={6} sm={4} md={3} lg={2} key={s.label}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            <Typography variant="h6">
              {typeof s.value === 'number' && s.type === 'currency' ? currency.format(s.value) : String(s.value)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const GroupTable: React.FC<{ groups: ReportResult['groups'] }> = ({ groups }) => {
  const metricKeys = Array.from(new Set(groups.flatMap((g) => Object.keys(g.metrics || {}))));
  const label = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  const money = (k: string) => /amount|balance|billed|collected/i.test(k);
  if (!groups.length) return null;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Group</TableCell>
            <TableCell align="right">Count</TableCell>
            {metricKeys.map((k) => <TableCell key={k} align="right">{label(k)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g) => (
            <TableRow key={g.label}>
              <TableCell>{g.label}</TableCell>
              <TableCell align="right">{g.count}</TableCell>
              {metricKeys.map((k) => (
                <TableCell key={k} align="right">
                  {g.metrics?.[k] === undefined ? '—' : money(k) ? currency.format(g.metrics[k]) : g.metrics[k]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ResultTable: React.FC<{ result: ReportResult }> = ({ result }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const rows = useMemo(() => {
    if (!sort) return result.rows;
    const col = result.columns.find((c) => c.key === sort.key);
    const val = (r: any) => {
      const v = r[sort.key];
      if (col?.type === 'number' || col?.type === 'currency') return Number(v) || 0;
      if (col?.type === 'date' || col?.type === 'datetime') return v ? new Date(v).getTime() : 0;
      return String(v ?? '').toLowerCase();
    };
    return [...result.rows].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const cmp = x < y ? -1 : x > y ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [result, sort]);

  const toggle = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 640 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {result.columns.map((c) => (
                <TableCell key={c.key} align={c.type === 'number' || c.type === 'currency' ? 'right' : 'left'} sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort?.key === c.key}
                    direction={sort?.key === c.key ? sort.dir : 'asc'}
                    onClick={() => toggle(c.key)}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r, i) => (
              <TableRow key={i} hover>
                {result.columns.map((c) => (
                  <TableCell key={c.key} align={c.type === 'number' || c.type === 'currency' ? 'right' : 'left'}>
                    {formatCell(r[c.key], c.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={result.columns.length || 1}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>No records match these filters.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100, 250]}
      />
    </Paper>
  );
};

// ---------------------------------------------------------------------------

const ReportCenterPage: React.FC = () => {
  const [selected, setSelected] = useState<string>('cases_by_attorney');
  const [filters, setFilters] = useState<Filters>({});
  const [period, setPeriod] = useState('all');
  const [result, setResult] = useState<ReportResult | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const { data: catalogData, isLoading: catalogLoading, error: catalogError } = useQuery({
    queryKey: ['report-center', 'catalog'],
    queryFn: () => getJson('/api/report-center/catalog', 'Unable to load reports'),
    staleTime: 10 * 60 * 1000,
  });
  const catalog: CatalogItem[] = catalogData?.data || [];
  const item = catalog.find((c) => c.key === selected) || catalog[0];

  const { data: optionsData } = useQuery({
    queryKey: ['report-center', 'options'],
    queryFn: () => getJson('/api/report-center/options', 'Unable to load filter options'),
    staleTime: 5 * 60 * 1000,
  });
  const options = optionsData?.data || { practiceAreas: [], jurisdictions: [] };

  const { data: staffData } = useQuery({ queryKey: ASSIGNABLE_USERS_QUERY_KEY, queryFn: fetchAssignableUsers, staleTime: 5 * 60 * 1000 });
  const staff = asAssignableUsers(staffData);

  const payload = () => {
    const { customFrom, customTo, ...rest } = filters;
    const range = item && (item.filters.includes('from') || item.filters.includes('to'))
      ? periodRange(period, customFrom, customTo)
      : {};
    const clean: Filters = {};
    Object.entries({ ...rest, ...range }).forEach(([k, v]) => {
      if (v !== '' && v !== undefined && v !== null && item?.filters.includes(k)) clean[k] = v;
    });
    return { filters: clean, tz: TZ };
  };

  const runMutation = useMutation({
    mutationFn: async () => {
      const res = await postJson(`/api/report-center/${item.key}/run`, payload(), 'Report failed');
      return (await res.json()).data as ReportResult;
    },
    onSuccess: (data) => setResult(data),
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Report failed')),
  });

  const doExport = async (formatKey: 'pdf' | 'xlsx' | 'csv') => {
    if (!item) return;
    setExporting(formatKey);
    try {
      const res = await postJson(`/api/report-center/${item.key}/export`, { ...payload(), format: formatKey }, 'Export failed');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const name = /filename="([^"]+)"/.exec(disposition)?.[1] || `${item.key}.${formatKey}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(null);
    }
  };

  const choose = (key: string) => {
    setSelected(key);
    setResult(null);
  };

  const categories = Array.from(new Set(catalog.map((c) => c.category)));

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Assessment /> Report Center
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Run firm reports and download them as PDF, Excel or CSV. Results follow your access — attorneys see their own matters.
      </Typography>

      {catalogLoading && <CircularProgress />}
      {catalogError && <Alert severity="error">{toUserErrorMessage(catalogError, 'Unable to load reports')}</Alert>}

      {item && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper variant="outlined">
              <List dense disablePadding>
                {categories.map((cat) => (
                  <React.Fragment key={cat}>
                    <ListSubheader>{cat}</ListSubheader>
                    {catalog.filter((c) => c.category === cat).map((c) => (
                      <ListItemButton key={c.key} selected={c.key === item.key} onClick={() => choose(c.key)}>
                        <ListItemText primary={c.title} />
                      </ListItemButton>
                    ))}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">{item.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{item.description}</Typography>
              <FilterBar
                item={item}
                filters={filters}
                setFilters={setFilters}
                period={period}
                setPeriod={setPeriod}
                staff={staff}
                options={options}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={runMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                  onClick={() => runMutation.mutate()}
                  disabled={runMutation.isPending}
                >
                  Run report
                </Button>
                <ButtonGroup variant="outlined" disabled={Boolean(exporting)}>
                  <Button startIcon={exporting === 'pdf' ? <CircularProgress size={14} /> : <PictureAsPdf />} onClick={() => doExport('pdf')}>PDF</Button>
                  <Button startIcon={exporting === 'xlsx' ? <CircularProgress size={14} /> : <TableChart />} onClick={() => doExport('xlsx')}>Excel</Button>
                  <Button startIcon={exporting === 'csv' ? <CircularProgress size={14} /> : <Download />} onClick={() => doExport('csv')}>CSV</Button>
                </ButtonGroup>
              </Stack>
            </Paper>

            {result && result.key === item.key && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">
                    {result.rows.length} row{result.rows.length === 1 ? '' : 's'} · generated {formatCell(result.generatedAt, 'datetime')}
                  </Typography>
                  {result.filterSummary && <Chip size="small" label={result.filterSummary} />}
                </Stack>
                {result.scopeNote && <Alert severity="info" sx={{ mb: 2 }}>{result.scopeNote}</Alert>}
                {result.truncated && (
                  <Alert severity="warning" sx={{ mb: 2 }}>Showing the first 5,000 rows — narrow the filters for a complete report.</Alert>
                )}
                <SummaryTiles summary={result.summary} />
                <GroupTable groups={result.groups} />
                <ResultTable result={result} />
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ReportCenterPage;
